import React, { useEffect, useState } from "react";
import { Timeline, TimelineEvent } from "react-event-timeline";
import {
  getDateIfValid,
  getSDCardErrorMessage,
  humanizeBytes,
  isDateBefore,
} from "../utils/common";
import { OTP_DATA_INITAL_STATE } from "../constants/schoolData";
import {
  TROUBLESHOOT_STEPS,
  TROUBLESHOOT_OPTIONS,
} from "../constants/troubleshootComponent";
import { useMutation } from "@apollo/client";
import { LATEST_OTP_QUERY } from "../services/query";
import { Button, Dropdown } from "react-bootstrap";
import NoData from "../assets/background/no-data.svg";
import { humanisetime } from "../utils/formatters";
import { triggerAction } from "../services/fetchApis";
import { EVENT_NAMES_CONSTANT, logFirebaseEvent } from "../services/events";
import moment from "moment";
import { ActionCommands, PROJECTS } from "../constants/Actions";

let successIcon = (
  <i
    style={{ fontSize: "10px" }}
    className="fa fa-check-circle text-success"
    aria-hidden="true"
  ></i>
);
let dangerIcon = (
  <i
    style={{ fontSize: "10px" }}
    className="fa fa-times-circle text-danger"
    aria-hidden="true"
  ></i>
);
let warnIcon = (
  <i
    style={{ fontSize: "10px" }}
    className="fa fa-exclamation-circle text-warning"
    aria-hidden="true"
  ></i>
);

const TroubleshootComponent = ({
  userData,
  nasPercent,
  isResourceDownloadingMultipleTimes,
  windowWidth,
  nasStatus,
}) => {
  const [showTroubleshoot, setShowTroubleshoot] = useState(undefined);
  const [troubleshootCount, setTroubleshootCount] = useState(0);
  const [timelineData, setTimelineData] = useState([]);
  const [otpData, setOtpData] = useState(OTP_DATA_INITAL_STATE);
  const [btnText, setBtnText] = useState("Trigger 1-click");
  const [oneClickResult, setOneClickResult] = useState({
    result: null,
    triggeredFrom: "",
  });

  const [getLatestOtp] = useMutation(LATEST_OTP_QUERY);

  useEffect(() => {
    let timer;
    let troubleShootSteps = [...TROUBLESHOOT_STEPS];

    if (
      showTroubleshoot &&
      !userData.status &&
      (showTroubleshoot?.type === "LOGIN_ISSUE" ||
        showTroubleshoot?.type === "OTHERS")
    ) {
      setTimelineData([TROUBLESHOOT_STEPS[0]]);
    } else if (showTroubleshoot) {
      if (showTroubleshoot?.type !== "OTHERS") {
        troubleShootSteps = troubleShootSteps.filter(
          (step) => step.type === showTroubleshoot.type
        );
      }

      if (showTroubleshoot) {
        troubleShootSteps.forEach((step, index) => {
          if (step.subType === "GET_OTP") {
            handleGetTeachersOtp(userData.teacherMobile);
          }
          timer = setTimeout(() => {
            setTimelineData((prevData) => [...prevData, step]);
          }, 600 * index);
        });
      }
    }

    return () => {
      clearTimeout(timer);
    };
  }, [troubleshootCount]);

  const getDropdownComponent = () => {
    return (
      <Dropdown>
        <Dropdown.Toggle
          className="troubleshoot-dropdown-btn"
          variant="info"
          id="troubleshoot-dropdown"
        >
          {showTroubleshoot?.name || "Select Issues"}
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {TROUBLESHOOT_OPTIONS.map((item, index) => {
            return (
              <Dropdown.Item
                key={`${item?.type}-${index + 1}`}
                onClick={() => onClickTrigger(item)}
              >
                {item?.name}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const onClickTrigger = (data) => {
    setTroubleshootCount(troubleshootCount + 1);
    setShowTroubleshoot(data);
    setTimelineData([]);
    logFirebaseEvent(EVENT_NAMES_CONSTANT.troubleshoot_triggered, {
      card_login_id: userData.teacherMobile,
      selected_option: data.name,
    });
  };

  const onClickOneClick = async (deviceToken, mobile, type = "") => {
    setBtnText("Running...");
    try {
      const result = await triggerAction(
        deviceToken,
        mobile,
        ActionCommands.ONE_CLICK,
        PROJECTS.TA
      );
      if (result?.status === "FALSE") {
        setBtnText("Trigger 1-click");
        setOneClickResult((prevState) => ({
          ...prevState,
          triggeredFrom: type,
          result: {
            type: "error",
            msg: result?.data?.results[0]?.error ?? "Failed to trigger 1-click",
          },
        }));
      } else {
        setTimeout(() => {
          setBtnText("Trigger 1-click");
        }, 10000);
        setOneClickResult((prevState) => ({
          ...prevState,
          triggeredFrom: type,
          result: {
            type: "success",
            msg: "1-click is triggered successfully",
          },
        }));
      }
    } catch (err) {
      console.log("One-click error", err);
      setBtnText("Trigger 1-click");
      setOneClickResult((prevState) => ({
        ...prevState,
        triggeredFrom: type,
        result: {
          type: "error",
          msg: "Failed to trigger 1-click",
        },
      }));
    }

    logFirebaseEvent(EVENT_NAMES_CONSTANT.one_click_triggered, {
      card_login_id: userData.teacherMobile,
      triggered_from: type,
    });
  };

  const handleGetTeachersOtp = async (mobile) => {
    try {
      const { data: result } = await getLatestOtp({
        variables: {
          input: {
            mobile: mobile || "",
          },
        },
      });
      setOtpData({ ...otpData, data: result.getLatestOtp });
    } catch (err) {
      if (err?.networkError && err?.networkError.result) {
        setOtpData({
          ...OTP_DATA_INITAL_STATE,
          error: err?.networkError.result?.msg,
        });
      } else {
        setOtpData({
          ...OTP_DATA_INITAL_STATE,
          error: "Unable to fetch data",
        });
      }
      console.log("Error in fetching latest OTP", err.message);
    }
  };

  const getResolveComponent = (data, type = "", subType = "") => {
    const filterData = [];

    /* remove duplicated 'triggers 1-click' CTA one troubleshoot step */
    data.forEach((item) => {
      const isPresent = filterData.find((item2) =>
        item2.type.includes("TRIGGER")
      );
      if (!isPresent) {
        filterData.push(item);
      }
    });
    return (
      <>
        {!!data.length && (
          <div style={{ fontWeight: "700", marginTop: "20px" }}>
            What can resolve
          </div>
        )}
        <ul>
          {filterData?.map((item, index) => {
            if (item?.type.includes("TRIGGER")) {
              return (
                <li key={`issues-${index + 1}`}>
                  {type === "LOGIN_ISSUE" ? (
                    <div className="dflex dflex-space-between">
                      <span>Get latest OTP:</span>
                      <Button
                        className="btn-trigger mr-5"
                        onClick={() =>
                          handleGetTeachersOtp(userData.teacherMobile)
                        }
                      >
                        {item.value}
                      </Button>
                    </div>
                  ) : (
                    <div className="dflex dflex-space-between">
                      {userData.buildNumber >= 432 && userData?.fcmToken && (
                        <span>Run 1-click:</span>
                      )}
                      {userData.buildNumber >= 432 && userData?.fcmToken ? (
                        <Button
                          className="btn-trigger mr-5"
                          onClick={() =>
                            onClickOneClick(
                              userData?.fcmToken,
                              userData?.teacherMobile,
                              subType
                            )
                          }
                        >
                          {btnText}
                        </Button>
                      ) : (
                        <div>
                          {subType === "TEACHER_FACING_ISSUE" ? (
                            <div>
                              Run 1-click to upgrade app, which should solve
                              duplicate downloads
                            </div>
                          ) : (
                            <div>Run 1-click</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            }
            return <li key={`issues-${index + 1}`}>{item.value}</li>;
          })}
        </ul>
      </>
    );
  };

  const getTimelineBodyComponent = (data) => {
    let icon = "";
    if (data.subType === "TEACHER_ACTIVE") {
      return (
        <div key={data.subType}>
          <span>{userData.status ? successIcon : dangerIcon}</span>
          {userData.status ? "Teacher is active" : "Teacher is inactive"}
        </div>
      );
    }

    if (data.subType === "GET_OTP") {
      const { data: otpDataObj, error } = otpData;
      return (
        <div key={data.subType}>
          {otpDataObj?.otpCode && (
            <div>
              <div>Last OTP Generated: {otpDataObj?.otpCode}</div>
              <div>Number of attempts: {otpDataObj?.attempts}</div>
              <div>Last OTP Generated: {otpDataObj?.otpCode}</div>
            </div>
          )}
          {error && (
            <div>
              <span>{dangerIcon}</span>
              {error}
            </div>
          )}
          {getResolveComponent(data.resolve, data.type)}
        </div>
      );
    }

    if (data.subType === "NAS_BOX") {
      if (nasPercent > 95) {
        icon = successIcon;
      } else if (nasPercent > 90) {
        icon = warnIcon;
      } else {
        icon = dangerIcon;
      }
      return (
        <div key={data.subType}>
          <div>
            <span>{icon}</span>
            <span>
              {nasPercent > 0
                ? `${nasPercent}% resources present`
                : "No resource present"}
            </span>
          </div>
          <>
            {(nasPercent < 100 ||
              nasStatus?.status?.toLocaleLowerCase() === "offline" ||
              !nasStatus?.status) && (
              <div style={{ fontWeight: "700", marginTop: "20px" }}>
                What can resolve
              </div>
            )}
            <ul>
              {data.resolve.map((item, index) => {
                if (
                  (item?.type.includes("TEXT_ONLINE") &&
                    nasStatus?.status?.toLocaleLowerCase() === "online" &&
                    nasPercent < 100) ||
                  (item?.type.includes("TEXT_OFFLINE") &&
                    (nasStatus?.status?.toLocaleLowerCase() === "offline" ||
                      !nasStatus?.status))
                ) {
                  // first condition: when nas is online but nas percentange is less than 100 show "what can resolve"
                  // second condition: when nas is offline but nas percentange show "what can resolve"
                  return (
                    <li key={`issue-resolve-${index + 1}`}>{item.value}</li>
                  );
                }

                return <span key={`issue-resolve-${index + 1}`}></span>;
              })}
            </ul>
          </>
        </div>
      );
    }

    if (data.subType === "SD_CARD") {
      if (userData.sdAvailableSpacePercentage > 50) {
        icon = successIcon;
      } else if (userData.sdAvailableSpacePercentage > 10) {
        icon = warnIcon;
      } else {
        icon = dangerIcon;
      }

      let resolvingSteps = [];
      for (let i = 0; i < data.resolve.length; i++) {
        const item = data.resolve[i];
        if (item.type === "TEXT_FORMAT" && !userData?.totalExternalStorage) {
          // first condition: when external space is not available show only one "what can resolve"
          resolvingSteps.push(item);
          break;
        } else if (
          (item.type === "TEXT_STORAGE_PERMISSION" &&
            userData?.totalExternalStorage &&
            userData.sdAvailableSpacePercentage <= 5 &&
            userData.canManageExternalStorage === 0) ||
          (item.type === "RED_RESOURCES_TRIGGER" &&
            userData?.teacherRedResources &&
            userData?.teacherRedResources > 0) ||
          (item.type === "TRIGGER" &&
            userData?.totalExternalStorage &&
            userData.sdAvailableSpacePercentage <= 5 &&
            userData.canManageExternalStorage === 1)
        ) {
          // first condition:  SD card is not corrupt, and not enough space available, and external storage permission is not provided "what can resolve"
          // second condition: if teacher has red resources then show "what can resolve"
          // third condition:  SD card is not corrupt, and not enough space available, and external storage permission is provided alow user to trigger one click
          resolvingSteps.push(item);
        }
      }

      return (
        <div key={data.subType}>
          <div>
            <span>{icon}</span>
            {`${humanizeBytes(
              userData?.availableExternalStorage
            )} free space available`}
          </div>
          {resolvingSteps?.map((item, index) => {
            return (
              <div>
                <span>{dangerIcon}</span>
                {item.value}
              </div>
            );
          })}
          {getSDCardErrorMessage(userData?.sdCardErrorMessage) && (
            <div>
              <span>{dangerIcon}</span>
              {userData?.sdCardErrorMessage}
            </div>
          )}
          {getResolveComponent(resolvingSteps, data.type, data.subType)}
          {oneClickResult.triggeredFrom === data.subType &&
            oneClickResult.result.type === "error" && (
              <div className="text-warning">{oneClickResult.result.msg}</div>
            )}
        </div>
      );
    }

    if (data.subType === "TEACHER_ONE_CLICK") {
      return (
        <div key={data.subType}>
          <div>
            <span>
              {!userData.isOneClickRequired ? successIcon : dangerIcon}
            </span>
            {userData.isOneClickRequired
              ? `The user hasn't performed a 1-click for more than ${
                  userData.oneClickRunAt
                    ? humanisetime(userData.oneClickRunAt)
                    : "30 Days"
                }. One click is required.`
              : "No 1-click action is required. The user has recently performed a 1-click action."}
          </div>
          {userData.isOneClickRequired &&
            getResolveComponent(data.resolve, "", data.subType)}
          {oneClickResult.triggeredFrom === data.subType && (
            <div
              className={
                oneClickResult.result.type === "error"
                  ? "text-danger"
                  : "text-success"
              }
            >
              {oneClickResult.result.msg}
            </div>
          )}
        </div>
      );
    }

    if (data.subType === "TEACHER_FACING_ISSUE") {
      return (
        <div key={data.subType}>
          <div>
            <span>
              {!isResourceDownloadingMultipleTimes ? successIcon : dangerIcon}
            </span>
            {isResourceDownloadingMultipleTimes
              ? "Duplicate resources are being downloaded. Utilizing 1-click can help resolve this issue."
              : "No 1-click action is required. The user has recently performed a 1-click action."}
          </div>
          {isResourceDownloadingMultipleTimes &&
            getResolveComponent(data.resolve, "", data.subType)}
          {oneClickResult.triggeredFrom === data.subType &&
            oneClickResult.result.type === "error" && (
              <div className="text-warning">{oneClickResult.result.msg}</div>
            )}
        </div>
      );
    }

    if (data.subType === "MAPPING_CHANGES") {
      const subjectUpdatedAt = getDateIfValid(userData.subjectUpdatedAt);
      const classUpdatedAt = getDateIfValid(userData.classUpdatedAt);
      const studentUpdatedAt = getDateIfValid(userData.studentUpdatedAt);

      let showError =
        isDateBefore(userData.oneClickRunAt, subjectUpdatedAt) ||
        isDateBefore(userData.oneClickRunAt, classUpdatedAt) ||
        isDateBefore(userData.oneClickRunAt, studentUpdatedAt);

      return (
        <div key={data.subType}>
          <div>
            {showError ? (
              <div>
                <span>{showError ? dangerIcon : successIcon}</span>
                Recent changes found in
                {classUpdatedAt &&
                  isDateBefore(userData.oneClickRunAt, classUpdatedAt) && (
                    <>
                      <br /> &emsp;&emsp;&emsp; Class allocation At:{" "}
                      <b>{classUpdatedAt}</b>
                    </>
                  )}
                {subjectUpdatedAt &&
                  isDateBefore(userData.oneClickRunAt, subjectUpdatedAt) && (
                    <>
                      <br /> &emsp;&emsp;&emsp; Subject allocation At:{" "}
                      <b>{subjectUpdatedAt}</b>
                    </>
                  )}
                {studentUpdatedAt &&
                  isDateBefore(userData.oneClickRunAt, studentUpdatedAt) && (
                    <>
                      <br /> &emsp;&emsp;&emsp; Student Mapping At:{" "}
                      <b>{studentUpdatedAt}</b>
                    </>
                  )}
                <br />
                To access the latest subject information trigger 1-click
              </div>
            ) : (
              "No 1-click action is required. The user has recently performed a 1-click action."
            )}
          </div>
          {getResolveComponent(data.resolve, "", data.subType)}
          {oneClickResult.triggeredFrom === data.subType &&
            oneClickResult.result.type === "error" && (
              <div className="text-warning">{oneClickResult.result.msg}</div>
            )}
        </div>
      );
    }

    return <></>;
  };

  return (
    <>
      <div className="dflex dflex-align-center dflex-justify-center dflex-column">
        <div
          className={`${
            windowWidth > 768 && "w-50"
          } font-weight-bold text-center troubleshoot-info-text`}
        >
          We're here to help! Experience any issues? Take advantage of our
          comprehensive troubleshooting feature by selecting the problem and
          clicking the Troubleshooting button below, and our step-by-step guide
          will lead you through resolving the problem with ease.
        </div>
        {getDropdownComponent()}
        <div>
          <br />
          Last One click At:<b> {userData?.oneClickRunAt}</b>
        </div>
      </div>

      {showTroubleshoot ? (
        <Timeline
          style={windowWidth > 768 ? { width: "50%", fontSize: "100%" } : {}}
        >
          {timelineData.map((data, index) => (
            <React.Fragment key={`${data.type}-${index + 1}`}>
              <TimelineEvent
                key={`${data.type}-${index + 1}`}
                title={data.title}
                icon={data.icon}
                iconStyle={{ fontSize: "14px" }}
                titleStyle={{ color: "#ff7929", fontSize: "100%" }} // Example styles
              >
                {getTimelineBodyComponent(data)}
              </TimelineEvent>
              <div
                style={{ margin: "8px 36px", borderBottom: "1px solid #ddd" }}
              ></div>
            </React.Fragment>
          ))}
        </Timeline>
      ) : (
        <div key="no-data">
          <img
            className="no-data-image my-5"
            src={NoData}
            alt="../../public/logo192.png"
          ></img>
        </div>
      )}
    </>
  );
};

export default TroubleshootComponent;
